import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import TutorialTargetIndicator from "./TutorialTargetIndicator";

function copyValue(value) {
  if (!value) return;
  navigator.clipboard?.writeText(String(value)).catch(() => {});
}

function normalizeText(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function isVisibleInteractionTarget(element) {
  if (!element?.isConnected || element.getClientRects().length === 0) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}

function overlapArea(first, second) {
  const width = Math.max(
    0,
    Math.min(first.right, second.right) - Math.max(first.left, second.left),
  );
  const height = Math.max(
    0,
    Math.min(first.bottom, second.bottom) - Math.max(first.top, second.top),
  );
  return width * height;
}

function expandedElementRect(element, padding = 22) {
  const rect = element.getBoundingClientRect();
  return {
    left: rect.left - padding,
    right: rect.right + padding,
    top: rect.top - padding,
    bottom: rect.bottom + padding,
  };
}

function findInteractionTarget(interaction, sample) {
  if (!interaction?.selector) return null;

  const elements = Array.from(document.querySelectorAll(interaction.selector));

  if (!interaction.textValueKey) {
    return elements.find(isVisibleInteractionTarget) || elements[0] || null;
  }

  const expected = normalizeText(sample?.[interaction.textValueKey]);

  const matchingElements = elements.filter((element) => {
    const context = interaction.contextSelector
      ? element.closest(interaction.contextSelector)
      : null;
    const dataName = normalizeText(
      [
        element.getAttribute("data-academy-contact-name"),
        element.getAttribute("data-academy-entity-title"),
        element.getAttribute("data-academy-entity-id"),
        element.getAttribute("data-academy-entity-ui-id"),
        context?.getAttribute("data-academy-contact-name"),
        context?.getAttribute("data-academy-entity-title"),
        context?.getAttribute("data-academy-entity-id"),
        context?.getAttribute("data-academy-entity-ui-id"),
      ]
        .filter(Boolean)
        .join(" "),
    );
    const elementText = normalizeText(
      [element.textContent, context?.textContent].filter(Boolean).join(" "),
    );
    return dataName.includes(expected) || elementText.includes(expected);
  });

  return (
    matchingElements.find(isVisibleInteractionTarget) ||
    matchingElements[0] ||
    null
  );
}

function sampleValue(sample, key) {
  if (!key) return undefined;
  return String(key)
    .split(".")
    .reduce((value, part) => value?.[part], sample);
}

function resolveTemplate(value, sample) {
  return String(value || "").replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key) =>
    encodeURIComponent(sampleValue(sample, key) ?? ""),
  );
}

function routeIsCurrent(route, location) {
  if (!route) return true;

  try {
    const target = new URL(route, window.location.origin);
    if (!location.pathname.startsWith(target.pathname)) return false;

    for (const [key, value] of target.searchParams.entries()) {
      if (new URLSearchParams(location.search).get(key) !== value) {
        return false;
      }
    }
    return true;
  } catch {
    return location.pathname.startsWith(String(route));
  }
}

function variantMatches(variant, sample, pathname) {
  if (!variant) return false;
  if (
    variant.whenSelectorExists &&
    !document.querySelector(variant.whenSelectorExists)
  ) {
    return false;
  }
  if (
    variant.whenSelectorMissing &&
    document.querySelector(variant.whenSelectorMissing)
  ) {
    return false;
  }
  if (
    variant.whenSampleTruthy &&
    !sampleValue(sample, variant.whenSampleTruthy)
  ) {
    return false;
  }
  if (variant.whenPathMatches) {
    try {
      if (!new RegExp(variant.whenPathMatches).test(pathname)) return false;
    } catch {
      if (!pathname.includes(variant.whenPathMatches)) return false;
    }
  }
  return Boolean(
    variant.whenSelectorExists ||
      variant.whenSelectorMissing ||
      variant.whenSampleTruthy ||
      variant.whenPathMatches,
  );
}

function completionReached(completion, sample, pathname) {
  if (!completion) return false;

  if (completion.type === "routeMatches") {
    try {
      return new RegExp(completion.pattern).test(pathname);
    } catch {
      return pathname.includes(String(completion.pattern || ""));
    }
  }

  if (completion.type === "selectorExists") {
    return Boolean(document.querySelector(completion.selector));
  }

  if (completion.type === "selectorMissing") {
    return !document.querySelector(completion.selector);
  }

  if (completion.type === "textExists") {
    const expected = normalizeText(
      completion.valueKey ? sample?.[completion.valueKey] : completion.value,
    );

    if (!expected) return false;

    const scope = completion.selector
      ? document.querySelector(completion.selector)
      : document.body;

    if (!scope) return false;
    return normalizeText(scope.innerText).includes(expected);
  }

  if (completion.type === "sampleTruthy") {
    return Boolean(sampleValue(sample, completion.key));
  }

  if (completion.type === "sampleEquals") {
    return (
      String(sampleValue(sample, completion.key)) === String(completion.value)
    );
  }

  if (completion.type === "all") {
    return (completion.conditions || []).every((condition) =>
      completionReached(condition, sample, pathname),
    );
  }

  if (completion.type === "any") {
    return (completion.conditions || []).some((condition) =>
      completionReached(condition, sample, pathname),
    );
  }

  return false;
}

export default function WorkflowTutorial({
  workflow,
  sample,
  stepIndex,
  onStepChange,
  onClose,
  onComplete,
  nextModule = null,
  completeLabel = "",
  completeError = "",
  completing = false,
  onWorkflowAction,
  actionLoading = false,
  actionError = "",
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [domRevision, setDomRevision] = useState(0);
  const [waitingForAction, setWaitingForAction] = useState(false);
  const [introLeaving, setIntroLeaving] = useState(false);
  const [dragGuide, setDragGuide] = useState(null);
  const [coachPosition, setCoachPosition] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);
  const advanceLockRef = useRef(false);
  const highlightedRef = useRef([]);
  const variantSignatureRef = useRef("");
  const coachRef = useRef(null);
  const introTimerRef = useRef(null);
  const preparedStepRef = useRef("");

  const steps = workflow?.steps || [];
  const baseStep = steps[stepIndex];

  useEffect(
    () => () => {
      const eventName = workflow?.cleanup?.dispatch;
      if (!eventName) return;

      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: workflow.cleanup.detail || {},
        }),
      );
    },
    [workflow?.cleanup?.detail, workflow?.cleanup?.dispatch],
  );

  useEffect(() => {
    const variants = Array.isArray(baseStep?.variants) ? baseStep.variants : [];

    const refreshVariant = () => {
      const matched = variants.find((variant) =>
        variantMatches(variant, sample, window.location.pathname),
      );

      const nextSignature =
        matched?.id ||
        matched?.whenSelectorExists ||
        matched?.whenSelectorMissing ||
        matched?.whenSampleTruthy ||
        matched?.whenPathMatches ||
        "default";
      if (variantSignatureRef.current === nextSignature) return;

      variantSignatureRef.current = nextSignature;
      setDomRevision((value) => value + 1);
    };

    variantSignatureRef.current = "";
    refreshVariant();

    // Observe only structural DOM changes. Watching attributes here caused
    // an infinite render loop when the tutorial itself added a highlight class.
    const observer = new MutationObserver(refreshVariant);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const timer = window.setInterval(refreshVariant, 900);

    return () => {
      observer.disconnect();
      window.clearInterval(timer);
    };
  }, [baseStep, location.pathname, sample, stepIndex]);

  const step = useMemo(() => {
    if (!baseStep) return null;

    const variants = Array.isArray(baseStep.variants) ? baseStep.variants : [];

    const matchedVariant = variants.find((variant) =>
      variantMatches(variant, sample, location.pathname),
    );

    if (!matchedVariant) return baseStep;

    return {
      ...baseStep,
      ...matchedVariant,
      variants: baseStep.variants,
    };
  }, [baseStep, domRevision, location.pathname, sample]);

  useEffect(() => {
    if (!step?.prepare) return undefined;

    const signature = `${workflow?.key || "workflow"}:${step.id || stepIndex}`;
    if (preparedStepRef.current === signature) return undefined;
    preparedStepRef.current = signature;

    const prepareStep = () => {
      if (
        step.prepare.ensure?.selector &&
        !document.querySelector(step.prepare.ensure.selector)
      ) {
        const ensureClickSelector = step.prepare.ensure.click;
        const ensureElements = ensureClickSelector
          ? Array.from(document.querySelectorAll(ensureClickSelector))
          : [];
        const ensureClickable =
          ensureElements.find(isVisibleInteractionTarget) ||
          ensureElements[0] ||
          null;
        ensureClickable?.click?.();
      }

      const clickSelectors = Array.isArray(step.prepare.click)
        ? step.prepare.click
        : [step.prepare.click].filter(Boolean);

      clickSelectors.forEach((selector) => {
        const elements = Array.from(document.querySelectorAll(selector));
        const clickable =
          elements.find(isVisibleInteractionTarget) || elements[0] || null;
        clickable?.click?.();
      });

      if (step.prepare.dispatch) {
        window.dispatchEvent(
          new CustomEvent(step.prepare.dispatch, {
            detail: step.prepare.detail || {},
          }),
        );
      }
    };

    prepareStep();
    const retry = window.setTimeout(prepareStep, 180);
    return () => window.clearTimeout(retry);
  }, [step, stepIndex, workflow?.key]);

  const goNextAutomatically = useCallback(() => {
    if (advanceLockRef.current) return;

    advanceLockRef.current = true;
    setWaitingForAction(false);

    window.setTimeout(() => {
      if (stepIndex >= steps.length - 1) {
        onComplete();
      } else {
        onStepChange(stepIndex + 1);
      }

      window.setTimeout(() => {
        advanceLockRef.current = false;
      }, 350);
    }, 220);
  }, [onComplete, onStepChange, stepIndex, steps.length]);

  useEffect(() => {
    advanceLockRef.current = false;
    setWaitingForAction(Boolean(step?.interaction));

    highlightedRef.current.forEach((element) => {
      element.classList.remove("academyWorkflowTarget");
      element.classList.remove("academyWorkflowTarget--action");
      element.removeAttribute("data-academy-workflow-active");
      element.removeAttribute("data-academy-workflow-action");
    });
    highlightedRef.current = [];
    setDragGuide(null);
    setActionTarget(null);

    const interaction = step?.interaction;
    if (!interaction) return undefined;

    let target = null;
    let clickHandler = null;
    let placementFrame = null;
    const targetWaitStartedAt = Date.now();

    const refreshCoachPosition = (elements) => {
      if (placementFrame != null) {
        window.cancelAnimationFrame(placementFrame);
      }

      placementFrame = window.requestAnimationFrame(() => {
        placementFrame = null;
        const coach = coachRef.current;
        if (!coach || stepIndex === 0) return;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const gap = viewportWidth <= 768 ? 12 : 20;
        const coachWidth = Math.min(
          coach.offsetWidth || 390,
          viewportWidth - gap * 2,
        );
        const coachHeight = Math.min(
          coach.offsetHeight || 420,
          viewportHeight - gap * 2,
        );
        const rightLeft = Math.max(
          gap,
          viewportWidth - gap - coachWidth,
        );
        const bottomTop = Math.max(
          gap,
          viewportHeight - gap - coachHeight,
        );

        // On a narrow screen the coach already occupies almost the whole
        // width, so moving it sideways cannot expose more of the target.
        if (viewportWidth <= 768) {
          setCoachPosition((previous) => {
            const next = { left: rightLeft, top: bottomTop };
            return previous &&
              Math.abs(previous.left - next.left) < 1 &&
              Math.abs(previous.top - next.top) < 1
              ? previous
              : next;
          });
          return;
        }

        const sidebarExpanded =
          document.body.getAttribute("data-sidebar") === "expanded";
        const preferredLeft = sidebarExpanded ? 300 : gap;
        const leftLeft = clamp(
          preferredLeft,
          gap,
          Math.max(gap, viewportWidth - gap - coachWidth),
        );
        const targetRects = elements
          .filter(isVisibleInteractionTarget)
          .map((element) => expandedElementRect(element));
        const candidates = [
          { left: rightLeft, top: bottomTop, preference: 0 },
          { left: leftLeft, top: bottomTop, preference: 1 },
          { left: rightLeft, top: gap, preference: 2 },
          { left: leftLeft, top: gap, preference: 3 },
        ].map((candidate) => {
          const candidateRect = {
            left: candidate.left,
            right: candidate.left + coachWidth,
            top: candidate.top,
            bottom: candidate.top + coachHeight,
          };
          return {
            ...candidate,
            overlap: targetRects.reduce(
              (total, rect) => total + overlapArea(candidateRect, rect),
              0,
            ),
          };
        });
        const best = candidates.reduce((selected, candidate) => {
          if (candidate.overlap < selected.overlap) return candidate;
          if (
            candidate.overlap === selected.overlap &&
            candidate.preference < selected.preference
          ) {
            return candidate;
          }
          return selected;
        }, candidates[0]);
        const next = { left: best.left, top: best.top };

        setCoachPosition((previous) =>
          previous &&
          Math.abs(previous.left - next.left) < 1 &&
          Math.abs(previous.top - next.top) < 1
            ? previous
            : next,
        );
      });
    };

    const refreshTarget = () => {
      target = findInteractionTarget(interaction, sample);

      if (!target && interaction.targetSelector) {
        target = document.querySelector(interaction.targetSelector);
      }

      const requiresDirectClick = interaction.type === "click";
      setActionTarget(requiresDirectClick && target ? target : null);

      const highlightSelectors = Array.isArray(interaction.highlightSelectors)
        ? interaction.highlightSelectors
        : [];
      const nextHighlighted = highlightSelectors.length
        ? highlightSelectors
            .map((selector) => {
              const elements = Array.from(document.querySelectorAll(selector));
              return (
                elements.find(isVisibleInteractionTarget) || elements[0] || null
              );
            })
            .filter(Boolean)
        : target
          ? [target]
          : [];
      const highlightChanged =
        highlightedRef.current.length !== nextHighlighted.length ||
        highlightedRef.current.some(
          (element, index) => element !== nextHighlighted[index],
        );

      if (interaction.highlight !== false && highlightChanged) {
        highlightedRef.current.forEach((element) => {
          element.classList.remove("academyWorkflowTarget");
          element.classList.remove("academyWorkflowTarget--action");
          element.removeAttribute("data-academy-workflow-active");
          element.removeAttribute("data-academy-workflow-action");
        });

        highlightedRef.current = nextHighlighted;
        nextHighlighted.forEach((element) => {
          element.classList.add("academyWorkflowTarget");
          element.setAttribute("data-academy-workflow-active", "true");
          if (requiresDirectClick) {
            element.classList.add("academyWorkflowTarget--action");
            element.setAttribute("data-academy-workflow-action", "click");
          }
        });
        nextHighlighted[0]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
      refreshCoachPosition(
        nextHighlighted.length ? nextHighlighted : target ? [target] : [],
      );

      if (interaction.type === "drag" && interaction.destinationSelector) {
        const destinationElements = Array.from(
          document.querySelectorAll(interaction.destinationSelector),
        );
        const destination =
          destinationElements.find(isVisibleInteractionTarget) ||
          destinationElements[0] ||
          null;

        if (target && destination) {
          const sourceRect = target.getBoundingClientRect();
          const destinationRect = destination.getBoundingClientRect();
          const sourceColumn = target.closest(
            '[data-academy="deal-stage-column"]',
          );
          const movingRight =
            destinationRect.left + destinationRect.width / 2 >
            sourceRect.left + sourceRect.width / 2;
          const startX = movingRight
            ? sourceRect.right + 7
            : sourceRect.left - 7;
          const startY = sourceRect.top + sourceRect.height / 2;
          const endX = movingRight
            ? destinationRect.left + 24
            : destinationRect.right - 24;
          const endY =
            destinationRect.top + Math.min(destinationRect.height * 0.55, 118);
          const stageName =
            destination.getAttribute("data-academy-stage-name") || "";
          const sourceStageName =
            sourceColumn?.getAttribute("data-academy-stage-name") || "";
          const nextGuide = {
            startX,
            startY,
            endX,
            endY,
              sourceLabel:
              (interaction.dragSourceLabel
                ? t(interaction.dragSourceLabel)
                : "") ||
              sourceStageName ||
              (interaction.dragSourceFallbackLabel
                ? t(interaction.dragSourceFallbackLabel)
                : "") ||
              t("academy_current_stage"),
              targetLabel:
                (interaction.dragTargetLabel
                  ? t(interaction.dragTargetLabel)
                  : "") ||
                stageName ||
                t("crm_settings_stage_qualified"),
            label:
              (interaction.dragGuideLabel
                ? t(interaction.dragGuideLabel)
                : "") ||
              t("academy_drag_here", {
                stage: stageName ? `: ${stageName}` : "",
              }),
          };

          setDragGuide((previous) => {
            if (
              previous &&
              Math.abs(previous.startX - nextGuide.startX) < 1 &&
              Math.abs(previous.startY - nextGuide.startY) < 1 &&
              Math.abs(previous.endX - nextGuide.endX) < 1 &&
              Math.abs(previous.endY - nextGuide.endY) < 1 &&
              previous.sourceLabel === nextGuide.sourceLabel &&
              previous.targetLabel === nextGuide.targetLabel &&
              previous.label === nextGuide.label
            ) {
              return previous;
            }
            return nextGuide;
          });
        } else {
          setDragGuide(null);
        }
      } else {
        setDragGuide(null);
      }
    };

    const checkCompletion = () => {
      if (
        completionReached(
          interaction.completion,
          sample,
          window.location.pathname,
        )
      ) {
        goNextAutomatically();
        return true;
      }
      return false;
    };

    refreshTarget();
    checkCompletion();

    if (interaction.type === "click" && interaction.autoAdvance !== false) {
      clickHandler = (event) => {
        refreshTarget();
        if (!target) return;

        if (event.target === target || target.contains(event.target)) {
          goNextAutomatically();
        }
      };
      document.addEventListener("click", clickHandler, true);
    }

    const observer = new MutationObserver(() => {
      refreshTarget();
      checkCompletion();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const interval = window.setInterval(() => {
      refreshTarget();
      if (checkCompletion()) return;

      if (
        !target &&
        interaction.skipIfMissing &&
        Date.now() - targetWaitStartedAt >=
          Number(interaction.missingTimeoutMs || 4500)
      ) {
        goNextAutomatically();
      }
    }, 600);
    window.addEventListener("resize", refreshTarget);
    window.addEventListener("scroll", refreshTarget, true);

    return () => {
      if (clickHandler) {
        document.removeEventListener("click", clickHandler, true);
      }
      observer.disconnect();
      window.clearInterval(interval);
      window.removeEventListener("resize", refreshTarget);
      window.removeEventListener("scroll", refreshTarget, true);
      if (placementFrame != null) {
        window.cancelAnimationFrame(placementFrame);
      }
      setDragGuide(null);
      setActionTarget(null);

      highlightedRef.current.forEach((element) => {
        element.classList.remove("academyWorkflowTarget");
        element.classList.remove("academyWorkflowTarget--action");
        element.removeAttribute("data-academy-workflow-active");
        element.removeAttribute("data-academy-workflow-action");
      });
      highlightedRef.current = [];
    };
  }, [goNextAutomatically, location.pathname, sample, step, stepIndex, t]);

  const isIntro = stepIndex === 0;
  const resolvedRoute = resolveTemplate(
    step?.routeTemplate || step?.route,
    sample,
  );

  useEffect(() => {
    if (isIntro) return undefined;

    const coach = coachRef.current;
    if (!coach) return undefined;

    let frame = null;
    const keepCoachInsideViewport = () => {
      if (frame != null) {
        window.cancelAnimationFrame(frame);
      }

      frame = window.requestAnimationFrame(() => {
        frame = null;
        const currentCoach = coachRef.current;
        if (!currentCoach) return;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const gap = viewportWidth <= 768 ? 12 : 20;
        const width = Math.min(
          currentCoach.offsetWidth || 390,
          viewportWidth - gap * 2,
        );
        const height = Math.min(
          currentCoach.offsetHeight || 420,
          viewportHeight - gap * 2,
        );
        const maximumLeft = Math.max(gap, viewportWidth - gap - width);
        const maximumTop = Math.max(gap, viewportHeight - gap - height);
        const fallback = {
          left: maximumLeft,
          top: maximumTop,
        };

        setCoachPosition((previous) => {
          const desired = previous || fallback;
          const next = {
            left: clamp(desired.left, gap, maximumLeft),
            top: clamp(desired.top, gap, maximumTop),
          };
          return previous &&
            Math.abs(previous.left - next.left) < 1 &&
            Math.abs(previous.top - next.top) < 1
            ? previous
            : next;
        });
      });
    };

    keepCoachInsideViewport();
    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(keepCoachInsideViewport)
        : null;
    resizeObserver?.observe(coach);
    window.addEventListener("resize", keepCoachInsideViewport);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", keepCoachInsideViewport);
      if (frame != null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [collapsed, isIntro, stepIndex]);

  useEffect(() => {
    if (
      !step?.autoNavigate ||
      !resolvedRoute ||
      routeIsCurrent(resolvedRoute, location)
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      navigate(resolvedRoute);
    }, 260);
    return () => window.clearTimeout(timer);
  }, [location, navigate, resolvedRoute, step?.autoNavigate]);

  useEffect(() => {
    if (!isIntro) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isIntro]);

  useEffect(
    () => () => {
      if (introTimerRef.current) {
        window.clearTimeout(introTimerRef.current);
      }
    },
    [],
  );

  const progress = useMemo(
    () => Math.round(((stepIndex + 1) / Math.max(steps.length, 1)) * 100),
    [stepIndex, steps.length],
  );

  if (!step) return null;

  const last = stepIndex === steps.length - 1;
  const routeDifferent =
    Boolean(resolvedRoute) && !routeIsCurrent(resolvedRoute, location);

  const handlePrimaryAction = () => {
    if (completing) return;

    if (last) {
      onComplete();
      return;
    }

    if (!isIntro) {
      onStepChange(stepIndex + 1);
      return;
    }

    if (introLeaving) return;

    const node = coachRef.current;
    if (node) {
      const rect = node.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = viewportWidth <= 768 ? 10 : 20;
      const dockWidth = Math.min(390, viewportWidth - gap * 2);
      const scale = Math.min(1, dockWidth / Math.max(rect.width, 1));
      const dockHeight = rect.height * scale;
      const targetLeft = viewportWidth - gap - dockWidth;
      const targetTop = viewportHeight - gap - dockHeight;

      node.style.setProperty(
        "--academy-intro-x",
        `${targetLeft - rect.left}px`,
      );
      node.style.setProperty("--academy-intro-y", `${targetTop - rect.top}px`);
      node.style.setProperty("--academy-intro-scale", String(scale));
    }

    setIntroLeaving(true);
    introTimerRef.current = window.setTimeout(() => {
      setIntroLeaving(false);
      onStepChange(stepIndex + 1);
    }, 520);
  };

  const handleWorkflowAction = async () => {
    const actionKey = step?.interaction?.actionKey;
    if (!actionKey || actionLoading || !onWorkflowAction) return;
    await onWorkflowAction(actionKey, step);
  };

  const dragGuidePath = dragGuide
    ? (() => {
        const distance = dragGuide.endX - dragGuide.startX;
        const bend = Math.max(54, Math.min(150, Math.abs(distance) * 0.38));
        const direction = distance >= 0 ? 1 : -1;
        const controlY = Math.min(dragGuide.startY, dragGuide.endY) - 58;
        return [
          `M ${dragGuide.startX} ${dragGuide.startY}`,
          `C ${dragGuide.startX + bend * direction} ${controlY},`,
          `${dragGuide.endX - bend * direction} ${controlY},`,
          `${dragGuide.endX} ${dragGuide.endY}`,
        ].join(" ");
      })()
    : "";

  return createPortal(
    <>
      <TutorialTargetIndicator
        target={actionTarget}
        active={!isIntro && Boolean(actionTarget)}
      />

      {dragGuide && (
        <div className="academyWorkflowDragGuide" aria-hidden="true">
          <svg width="100%" height="100%">
            <defs>
              <marker
                id="academyWorkflowArrowHead"
                markerWidth="12"
                markerHeight="12"
                refX="9"
                refY="6"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M 0 0 L 10 6 L 0 12 z" />
              </marker>
            </defs>
            <path className="academyWorkflowDragGuideRail" d={dragGuidePath} />
            <path
              className="academyWorkflowDragGuideMotion"
              d={dragGuidePath}
              markerEnd="url(#academyWorkflowArrowHead)"
            />
            <g className="academyWorkflowDragGhostCard">
              <animateMotion
                dur="2.35s"
                repeatCount="indefinite"
                path={dragGuidePath}
                rotate="0"
              />
              <animate
                attributeName="opacity"
                values="0;1;1;0"
                keyTimes="0;0.12;0.82;1"
                dur="2.35s"
                repeatCount="indefinite"
              />
              <rect x="-40" y="-18" width="80" height="36" rx="9" />
              <circle cx="-27" cy="-5" r="4" />
              <path d="M -17 -7 H 28 M -17 1 H 19 M -27 9 H 11" />
              <text x="-17" y="12">
                {step?.interaction?.dragEntityLabel
                  ? t(step.interaction.dragEntityLabel)
                  : "Deal"}
              </text>
            </g>
            <circle
              className="academyWorkflowDragGuideOrigin"
              cx={dragGuide.startX}
              cy={dragGuide.startY}
              r="7"
            />
          </svg>
          <div
            className="academyWorkflowDragGuideLabel"
            style={{
              left: `${Math.max(
                92,
                Math.min(window.innerWidth - 92, dragGuide.endX),
              )}px`,
              top: `${Math.max(34, dragGuide.endY - 52)}px`,
            }}
          >
            <span>↗</span>
            {dragGuide.label}
          </div>
        </div>
      )}

      {isIntro && (
        <div
          className={`academyWorkflowIntroBackdrop ${
            introLeaving ? "is-leaving" : ""
          }`}
          aria-hidden="true"
        />
      )}

      <aside
        ref={coachRef}
        className={`academyWorkflowCoach ${
          collapsed ? "is-collapsed" : ""
        } ${isIntro ? "is-intro" : ""} ${
          introLeaving ? "is-intro-leaving" : ""
        } ${coachPosition && !isIntro ? "has-adaptive-position" : ""}`}
        style={
          coachPosition && !isIntro
            ? {
                "--academy-coach-left": `${coachPosition.left}px`,
                "--academy-coach-top": `${coachPosition.top}px`,
              }
            : undefined
        }
        aria-label={t(workflow.title)}
        aria-modal={isIntro ? "true" : undefined}
        role={isIntro ? "dialog" : "complementary"}
      >
        <header className="academyWorkflowCoachHeader">
          <div>
            <small>
              {t("academy_practical_training")}
              {Number(workflow?.moduleCount || 0) > 1 && (
                <em className="academyWorkflowModuleCounter">
                  {t("academy_stage_progress", {
                    current: workflow.moduleIndex,
                    total: workflow.moduleCount,
                  })}
                </em>
              )}
            </small>
            <strong>{t(workflow.title)}</strong>
          </div>

          <div className="academyWorkflowCoachHeaderActions">
            {!isIntro && (
              <button
                type="button"
                onClick={() => setCollapsed((value) => !value)}
                title={collapsed ? t("expand") : t("collapse")}
              >
                {collapsed ? "▢" : "—"}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              title={t("academy_close_training")}
            >
              ×
            </button>
          </div>
        </header>

        {!collapsed && (
          <>
            <div className="academyWorkflowProgress">
              <div>
                <span>
                  {t("academy_step_progress", {
                    current: stepIndex + 1,
                    total: steps.length,
                  })}
                </span>
                <b>{progress}%</b>
              </div>
              <i>
                <span style={{ width: `${progress}%` }} />
              </i>
            </div>

            <div className="academyWorkflowBody">
              {isIntro && (
                <div className="academyWorkflowIntroVisual">
                  <span>{t("CRM")}</span>
                  <div>
                    <small>{t("academy_interactive_practice")}</small>
                    <strong>
                      {workflow.pathLabel
                        ? t(workflow.pathLabel)
                        : t("academy_workflow_pathLabel_001")}
                    </strong>
                  </div>
                </div>
              )}

              <h3>{t(step.title)}</h3>
              <p>{t(step.body)}</p>

              {last && nextModule && (
                <section
                  className="academyWorkflowNextStage"
                  aria-label={t("academy_next_training_stage")}
                >
                  <div
                    className="academyWorkflowNextStageRail"
                    aria-hidden="true"
                  >
                    <span className="is-complete">
                      ✓<small>{t("academy_stage_number", { number: 1 })}</small>
                    </span>
                    <i />
                    <span className="is-next">
                      {nextModule.index || 2}
                      <small>
                        {t("academy_stage_number", {
                          number: nextModule.index || 2,
                        })}
                      </small>
                    </span>
                  </div>
                  <div className="academyWorkflowNextStageCopy">
                    <small>
                      {t("academy_next_stage_progress", {
                        current: nextModule.index || 2,
                        total: nextModule.total || 2,
                      })}
                    </small>
                    <strong>{t(nextModule.title)}</strong>
                    <p>{t(nextModule.body)}</p>
                    <b>{t("academy_contact_history_preserved")}</b>
                    {completeError && (
                      <span className="academyWorkflowNextStageError">
                        {completeError}
                      </span>
                    )}
                  </div>
                </section>
              )}

              {step.interaction?.type === "drag" && (
                <div
                  className="academyWorkflowDragDemo"
                  aria-label={t("academy_drag_example", {
                    entity: step.interaction.dragEntityLabel
                      ? t(step.interaction.dragEntityLabel)
                      : t("deal"),
                    source:
                      dragGuide?.sourceLabel ||
                      (step.interaction.dragSourceLabel
                        ? t(step.interaction.dragSourceLabel)
                        : "") ||
                      (step.interaction.dragSourceFallbackLabel
                        ? t(step.interaction.dragSourceFallbackLabel)
                        : "") ||
                      t("academy_current_stage"),
                    target:
                      dragGuide?.targetLabel ||
                      (step.interaction.dragTargetLabel
                        ? t(step.interaction.dragTargetLabel)
                        : "") ||
                      t("crm_settings_stage_qualified"),
                  })}
                >
                  <span className="academyWorkflowDragDemoSource">
                    {dragGuide?.sourceLabel ||
                      (step.interaction.dragSourceLabel
                        ? t(step.interaction.dragSourceLabel)
                        : "") ||
                      (step.interaction.dragSourceFallbackLabel
                        ? t(step.interaction.dragSourceFallbackLabel)
                        : "") ||
                      t("academy_current_stage")}
                  </span>
                  <span className="academyWorkflowDragDemoTrack">
                    <i aria-hidden="true">→</i>
                    <b>{t("deal")}</b>
                  </span>
                  <span className="academyWorkflowDragDemoTarget">
                    {dragGuide?.targetLabel ||
                      (step.interaction.dragTargetLabel
                        ? t(step.interaction.dragTargetLabel)
                        : "") ||
                      t("crm_settings_stage_qualified")}
                  </span>
                </div>
              )}

              {step.fields?.length > 0 && (
                <div className="academyWorkflowFields">
                  {step.fields.map((field) => {
                    const value =
                      field.valueKey != null
                        ? sample?.[field.valueKey]
                        : field.valueTranslation
                          ? t(field.valueTranslation)
                        : field.value;

                    return (
                      <div key={`${field.label}-${field.valueKey || value}`}>
                        <span>{t(field.label)}</span>
                        <strong>{value}</strong>
                        <button
                          type="button"
                          onClick={() => copyValue(value)}
                          title={t("copy")}
                        >
                          ⧉
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {step.checklist?.length > 0 && (
                <div className="academyWorkflowChecklist">
                  {step.checklist.map((item, index) => (
                    <label key={`${step.id}-${index}`}>
                      <input type="checkbox" />
                      <span>{t(item)}</span>
                    </label>
                  ))}
                </div>
              )}

              {step.targetHint && (
                <div className="academyWorkflowHint">
                  <span>→</span>
                  <p>{t(step.targetHint)}</p>
                </div>
              )}

              {step.interaction?.type === "action" && (
                <div className="academyWorkflowActionCard">
                  <div>
                    <span aria-hidden="true">◎</span>
                    <p>
                      {t("academy_controlled_demo_notice")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleWorkflowAction}
                    disabled={actionLoading}
                  >
                    {actionLoading
                      ? t("academy_preparing")
                      : step.interaction.actionLabel
                        ? t(step.interaction.actionLabel)
                        : t("academy_execute")}
                  </button>
                  {actionError && (
                    <small className="academyWorkflowActionError">
                      {actionError}
                    </small>
                  )}
                </div>
              )}

              {step.interaction && (
                <div className="academyWorkflowAutoStatus">
                  <span className={waitingForAction ? "is-waiting" : ""}>
                    {waitingForAction ? "●" : "✓"}
                  </span>
                  <p>
                    {step.interaction.statusText
                      ? t(step.interaction.statusText)
                      : t("academy_complete_highlighted_action")}
                  </p>
                </div>
              )}

              {routeDifferent && (
                <button
                  type="button"
                  className="academyWorkflowRouteButton"
                  onClick={() => navigate(resolvedRoute)}
                >
                  {step.actionLabel
                    ? t(step.actionLabel)
                    : t("academy_go_to_required_page")}{" "}
                  →
                </button>
              )}
            </div>

            <footer className="academyWorkflowFooter">
              {isIntro ? (
                <span className="academyWorkflowIntroFootnote">
                  {workflow.introFootnote
                    ? t(workflow.introFootnote)
                    : t("academy_intro_panel_hint")}
                </span>
              ) : (
                <span className="academyWorkflowLinearNote">
                  {t("academy_completed_stages_not_repeated")}
                </span>
              )}

              <button
                type="button"
                className="academyPrimaryButton"
                disabled={
                  completing ||
                  introLeaving ||
                  (Boolean(step.interaction) &&
                    !step.interaction.allowManualSkip)
                }
                onClick={handlePrimaryAction}
              >
                {isIntro
                  ? introLeaving
                    ? t("academy_starting")
                    : workflow.introActionLabel
                      ? t(workflow.introActionLabel)
                      : t("academy_start_tutorial")
                  : last
                    ? completing
                      ? t("academy_preparing_next_stage")
                      : completeLabel
                        ? t(completeLabel)
                        : t("academy_finish_training")
                    : step.interaction && !step.interaction.allowManualSkip
                      ? t("academy_waiting_for_action")
                      : step.interaction
                        ? step.interaction.skipLabel
                          ? t(step.interaction.skipLabel)
                          : t("academy_skip")
                        : t("academy_done_continue")}
              </button>
            </footer>
          </>
        )}
      </aside>
    </>,
    document.body,
  );
}
